import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Save, Globe, Clock as ClockIcon, FileText, DollarSign, Webhook, Phone, Mail, Bell, ArrowRightLeft, Calendar, Users, CheckCircle, Loader, Zap, XCircle, UserPlus, X, Lock, KeyRound } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BusinessHoursEditor from '../components/BusinessHoursEditor';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [company, setCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const validTabs = ['info', 'contact', 'webhook', 'security', 'team'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabFromUrl) ? tabFromUrl : 'info');

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'user' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    business_hours: '', faqs: '', services: '', pricing: '', webhook_url: '',
    contact_email: '', contact_phone: '', callback_number: '',
    transfer_number: '',
    escalation_rules: '', booking_prefs: '',
    notification_prefs: { email: true, sms: false, weekly_digest: true },
    missed_call_textback: false, textback_message: '',
    hourly_rate: ''
  });

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get('/api/settings/company');
        // /api/settings/company returns settings/form fields only.
        // Pull the full company record (name, phone_number, created_at, ...) from /api/company.
        try {
          const companyRes = await api.get('/api/company');
          setCompany(companyRes.data);
        } catch {
          // Fallback: at least keep something so the header doesn't crash.
          setCompany({ name: data.company_name, phone_number: '', created_at: new Date().toISOString() });
        }
        setForm({
          business_hours: data.business_hours || '',
          faqs: data.faqs || '',
          services: data.services || '',
          pricing: data.pricing || '',
          webhook_url: data.webhook_url || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          callback_number: data.callback_number || '',
          transfer_number: data.transfer_number || '',
          escalation_rules: data.escalation_rules || '',
          booking_prefs: data.booking_prefs || '',
          notification_prefs: data.notification_prefs || { email: true, sms: false, weekly_digest: true },
          missed_call_textback: data.missed_call_textback || false,
          textback_message: data.textback_message || '',
          hourly_rate: data.hourly_rate != null ? String(data.hourly_rate) : '50',
        });
        // Fetch team using client-accessible endpoint
        try {
          const teamRes = await api.get('/api/settings/team');
          setCompanyUsers(teamRes.data.team || []);
        } catch { }
        // Fetch webhook logs
        try {
          const logsRes = await api.get('/api/settings/webhook-logs');
          setWebhookLogs(logsRes.data.logs || []);
        } catch { }
      } catch (err) {
        toast.error('Kunne ikke laste innstillinger');
      } finally { setLoading(false); }
    }
    load();
  }, [user]); // eslint-disable-line

  // Validation helpers
  const validateNoPhone = (val) => {
    if (!val || !val.trim()) return null;
    const cleaned = val.replace(/[\s-]/g, '');
    if (!/^\+47\d{8}$/.test(cleaned)) return 'Må være på formatet +47XXXXXXXX (8 siffer)';
    return null;
  };
  const validateUrl = (val) => {
    if (!val || !val.trim()) return null;
    try {
      const u = new URL(val);
      if (!['http:', 'https:'].includes(u.protocol)) return 'URL må starte med http:// eller https://';
      return null;
    } catch {
      return 'Ugyldig URL';
    }
  };
  const validateEmail = (val) => {
    if (!val || !val.trim()) return null;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return 'Ugyldig e-postadresse';
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Run validations on phone-like fields, webhook URL, email
    const errors = [];
    const phoneErr1 = validateNoPhone(form.contact_phone);
    const phoneErr2 = validateNoPhone(form.callback_number);
    const phoneErr3 = validateNoPhone(form.transfer_number);
    const urlErr = validateUrl(form.webhook_url);
    const emailErr = validateEmail(form.contact_email);
    if (phoneErr1) errors.push(`Kontakttelefon: ${phoneErr1}`);
    if (phoneErr2) errors.push(`Tilbakeringingsnummer: ${phoneErr2}`);
    if (phoneErr3) errors.push(`Videresend til: ${phoneErr3}`);
    if (urlErr) errors.push(`Webhook URL: ${urlErr}`);
    if (emailErr) errors.push(`Kontakt-e-post: ${emailErr}`);
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/api/settings/company', form);
      // Backend returns {ok: true}; refetch the full company record so we don't
      // overwrite `company` state with {ok:true} and break the header (name, phone, etc).
      try {
        const companyRes = await api.get('/api/company');
        setCompany(companyRes.data);
      } catch { /* keep existing company state */ }
      setSaved(true);
      toast.success('Innstillinger lagret');
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      toast.error('Kunne ikke lagre innstillinger');
    } finally { setSaving(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteError('');
    try {
      await api.post('/api/company/users/invite', inviteForm);
      toast.success(`Invitasjon sendt til ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ name: '', email: '', role: 'user' });
      // Refresh team list
      try {
        const teamRes = await api.get('/api/settings/team');
        setCompanyUsers(teamRes.data.team || []);
      } catch { /* no-op */ }
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Kunne ikke sende invitasjon');
    } finally {
      setInviting(false);
    }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    setWebhookResult(null);
    try {
      const { data } = await api.post('/api/settings/webhook-test', { company_id: company?._id });
      setWebhookResult(data);
      if (data.success) toast.success(`Webhook levert (${data.status_code})`);
      else toast.error(`Webhook feilet: ${data.error || 'Ukjent feil'}`);
      // Refresh logs
      const logsRes = await api.get('/api/settings/webhook-logs');
      setWebhookLogs(logsRes.data.logs || []);
    } catch (err) {
      setWebhookResult({ success: false, error: 'Kunne ikke teste webhook' });
      toast.error('Kunne ikke teste webhook');
    } finally { setTestingWebhook(false); }
  };

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-navy-800 rounded w-48" />
      <div className="h-20 bg-navy-800 rounded-lg" />
      <div className="h-12 bg-navy-800 rounded-lg w-80" />
      <div className="h-96 bg-navy-800 rounded-lg" />
    </div>
  );

  const tabs = [
    { id: 'info', label: 'Bedriftsinformasjon', icon: Globe },
    { id: 'contact', label: 'Kontakt og varsler', icon: Bell },
    { id: 'webhook', label: 'Webhook', icon: Webhook },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'security', label: 'Sikkerhet', icon: Lock },
  ];

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('Nye passord stemmer ikke overens');
      return;
    }
    if ((pwForm.new_password || '').length < 8) {
      setPwError('Nytt passord må være minst 8 tegn');
      return;
    }
    setPwSaving(true);
    try {
      await api.post('/api/client/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Passord oppdatert. Du vil bli logget ut.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(async () => {
        try { await logout(); } catch (_) { /* no-op */ }
        navigate('/login');
      }, 1500);
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Kunne ikke oppdatere passord');
    } finally {
      setPwSaving(false);
    }
  };

  const phoneFields = ['contact_phone', 'callback_number', 'transfer_number'];
  const handleFieldBlur = (field, value) => {
    if (phoneFields.includes(field)) {
      const err = validateNoPhone(value);
      setFieldErrors(prev => ({ ...prev, [field]: err }));
    } else if (field === 'contact_email') {
      const err = validateEmail(value);
      setFieldErrors(prev => ({ ...prev, [field]: err }));
    } else if (field === 'webhook_url') {
      const err = validateUrl(value);
      setFieldErrors(prev => ({ ...prev, [field]: err }));
    }
  };

  const Field = ({ label, icon: Icon, field, multiline, placeholder, type = 'text', helpText }) => {
    const err = fieldErrors[field];
    const borderCls = err
      ? 'border-accent-red focus:ring-accent-red'
      : 'border-navy-600 focus:ring-accent-blue';
    return (
    <div>
      <label className="flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        <Icon size={14} /> {label}
      </label>
      {multiline ? (
        <textarea data-testid={`settings-${field}`} value={form[field] || ''}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          onBlur={(e) => handleFieldBlur(field, e.target.value)}
          rows={4} placeholder={placeholder}
          className={`w-full px-4 py-3 bg-navy-800 border rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 resize-none ${borderCls}`} />
      ) : (
        <input data-testid={`settings-${field}`} type={type} value={form[field] || ''}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          onBlur={(e) => handleFieldBlur(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full px-4 py-3 bg-navy-800 border rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 ${borderCls}`} />
      )}
      {err && <p className="text-xs text-accent-red mt-1.5 flex items-center gap-1"><XCircle size={12}/> {err}</p>}
      {!err && helpText && <p className="text-xs text-slate-500 mt-1.5">{helpText}</p>}
    </div>
  );
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Innstillinger</h1>
        <p className="text-slate-500 text-sm mt-1">Oppdater bedriftsinformasjonen som AI-resepsjonisten bruker</p>
      </div>

      {company && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5 mb-6 flex items-center gap-4">
          <div className="w-11 h-11 bg-accent-blue/20 rounded-lg flex items-center justify-center">
            <Globe size={18} className="text-accent-blue" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-white">{company.name}</h2>
            <p className="text-sm text-slate-400">Telefon: {company.phone_number}</p>
          </div>
          {saved && (
            <div className="flex items-center gap-2 text-accent-green animate-fade-in" data-testid="settings-saved-message">
              <CheckCircle size={16} />
              <span className="text-sm font-medium">Endringer lagret!</span>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => handleTabChange(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === id ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSave}>
        {activeTab === 'info' && (
          <div className="space-y-6">
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
              <h3 className="font-heading text-base font-semibold text-white">Bedriftsinformasjon</h3>
              <p className="text-sm text-slate-400 -mt-2">Denne informasjonen brukes av AI-resepsjonisten til å svare på spørsmål.</p>
              <Field icon={ClockIcon} label="Åpningstider" field="business_hours" placeholder="Man–Fre: 08:00–16:00, Lør: 09:00–13:00" />
              <BusinessHoursEditor value={form.business_hours} onChange={(v) => setForm({ ...form, business_hours: v })} />
              <Field icon={FileText} label="Ofte stilte spørsmål (FAQ)" field="faqs" multiline placeholder="Skriv inn vanlige spørsmål og svar..." />
              <Field icon={Globe} label="Tjenester" field="services" multiline placeholder="Liste over tjenester bedriften tilbyr..." />
              <Field icon={DollarSign} label="Priser" field="pricing" multiline placeholder="Prisinformasjon for tjenester..." />
              <Field icon={DollarSign} label="Timepris (USD) — for ROI-beregning" field="hourly_rate" type="number" placeholder="50" helpText="Brukes til å beregne estimert kostnadsbesparelse i ROI-rapporten." />
            </div>

            {/* AI Preview */}
            {(form.business_hours || form.services) && (
              <div className="bg-navy-800 border border-accent-blue/20 rounded-lg p-6">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-accent-blue mb-3">Slik vil resepsjonisten svare</h3>
                <div className="bg-navy-900 rounded-lg p-4 text-sm text-slate-300 leading-relaxed italic">
                  "Hei! {company?.name || 'Vi'} har åpent {form.business_hours || 'mandag til fredag'}.
                  {form.services ? ` Vi tilbyr ${form.services.split(',')[0]?.trim()}.` : ''}
                  {form.pricing ? ` Prisene starter fra ${form.pricing.split(',')[0]?.trim()}.` : ''}
                  Ønsker du å bestille en time?"
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
            <h3 className="font-heading text-base font-semibold text-white">Kontaktinformasjon</h3>
            <Field icon={Mail} label="Kontakt-e-post" field="contact_email" placeholder="post@bedrift.no" type="email" />
            <Field icon={Phone} label="Kontakttelefon" field="contact_phone" placeholder="+47 123 45 678" />
            <Field icon={Phone} label="Foretrukket tilbakeringingsnummer" field="callback_number" placeholder="+47 123 45 678"
              helpText="Dette nummeret brukes når kunder ber om å bli ringt tilbake." />
            <Field icon={ArrowRightLeft} label="Videresend til" field="transfer_number" placeholder="+47 XXX XX XXX"
              helpText="Nummeret som brukes når innringer ber om å snakke med et menneske." />

            <div className="border-t border-navy-600 pt-5">
              <h3 className="font-heading text-base font-semibold text-white mb-4">Varselinnstillinger</h3>
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.notification_prefs?.email || false}
                    onChange={(e) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, email: e.target.checked } })}
                    className="w-4 h-4 mt-0.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue" />
                  <div>
                    <span className="text-sm text-white">E-postvarsler for nye samtaler</span>
                    <p className="text-xs text-slate-500 mt-0.5">Du mottar en e-post hver gang en ny samtale er registrert.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.notification_prefs?.weekly_digest !== false}
                    onChange={(e) => setForm({ ...form, notification_prefs: { ...form.notification_prefs, weekly_digest: e.target.checked } })}
                    data-testid="notif-weekly-digest"
                    className="w-4 h-4 mt-0.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue" />
                  <div>
                    <span className="text-sm text-white">Ukentlig sammendrag</span>
                    <p className="text-xs text-slate-500 mt-0.5">Send meg en ukentlig oppsummering av samtaler hver mandag morgen.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Coming-soon section: visually distinct, locked card so users can see what's planned */}
            <div className="mt-6 rounded-lg border border-dashed border-navy-600 bg-navy-900/40 p-5 opacity-80">
              <div className="flex items-center gap-2 mb-4">
                <Lock size={14} className="text-slate-500" />
                <h3 className="font-heading text-sm font-semibold text-slate-300 tracking-wide">Kommer snart 🚧</h3>
              </div>
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-not-allowed">
                  <input type="checkbox" checked={false} disabled onChange={() => {}}
                    className="w-4 h-4 mt-0.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue opacity-50" />
                  <div>
                    <span className="text-sm text-slate-400">SMS-varsler for varme leads</span>
                    <p className="text-xs text-slate-500 mt-0.5">Du mottar en SMS når en lead med score 80+ registreres.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-not-allowed">
                  <input type="checkbox" checked={false} disabled onChange={() => {}}
                    className="w-4 h-4 mt-0.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue opacity-50" />
                  <div>
                    <span className="text-sm text-slate-400">Automatisk SMS ved tapte anrop</span>
                    <p className="text-xs text-slate-500 mt-0.5">Sender automatisk en SMS til innringere som legger på innen 15 sekunder uten å komme gjennom.</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="border-t border-navy-600 pt-5">
              <Field icon={ArrowRightLeft} label="Eskaleringsregler" field="escalation_rules" multiline
                placeholder="Definer når samtaler skal overføres til en ansatt, f.eks. «Ved klager med haster-score over 7, overfør til +4712345678»"
                helpText="Disse reglene forteller AI-resepsjonisten når den skal overføre samtalen til et menneske." />
              <div className="mt-5">
                <Field icon={Calendar} label="Bestillingspreferanser" field="booking_prefs" multiline
                  placeholder="Foretrukket timeplanlegging, f.eks. «Minimum 30 min mellom avtaler, ingen bookinger etter kl 15:00 på fredager»" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'webhook' && (
          <div className="space-y-6">
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-5">
              <h3 className="font-heading text-base font-semibold text-white">Webhook-innstillinger</h3>
              <p className="text-sm text-slate-400">Konfigurer en webhook-URL for å motta samtaledata i sanntid. Data sendes som JSON POST til URLen du oppgir.</p>
              <Field icon={Webhook} label="Webhook URL" field="webhook_url" placeholder="https://din-tjeneste.no/webhook" />

              {form.webhook_url && (
                <div className="flex items-center gap-3">
                  <button type="button" onClick={testWebhook} disabled={testingWebhook} data-testid="webhook-test-button"
                    className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 border border-navy-600 text-white text-sm rounded-lg hover:border-accent-blue transition-colors disabled:opacity-50">
                    {testingWebhook ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
                    {testingWebhook ? 'Tester...' : 'Test webhook'}
                  </button>
                  {webhookResult && (
                    <span className={`flex items-center gap-1.5 text-sm ${webhookResult.success ? 'text-accent-green' : 'text-accent-red'}`}>
                      {webhookResult.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {webhookResult.success ? `Levert (HTTP ${webhookResult.status_code})` : webhookResult.error || 'Feilet'}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Webhook delivery log */}
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
              <h3 className="font-heading text-sm font-semibold text-white mb-4">Siste leveringer</h3>
              {webhookLogs.length > 0 ? (
                <div className="space-y-2">
                  {webhookLogs.map(log => (
                    <div key={log._id} className="flex items-center justify-between p-3 bg-navy-900 rounded-lg">
                      <div className="flex items-center gap-3">
                        {log.success ? <CheckCircle size={14} className="text-accent-green" /> : <XCircle size={14} className="text-accent-red" />}
                        <div>
                          <span className="text-sm text-white">{log.event}</span>
                          <p className="text-xs text-slate-500">{log.created_at ? new Date(log.created_at).toLocaleString('nb-NO') : '—'}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-mono ${log.success ? 'text-accent-green' : 'text-accent-red'}`}>
                        {log.status_code || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Webhook size={24} className="mx-auto text-slate-600 mb-2" />
                  <p className="text-sm text-slate-500">Ingen webhook-leveringer ennå.</p>
                  <p className="text-xs text-slate-600 mt-1">Konfigurer en webhook-URL og test den for å se leveringsloggen her.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={18} className="text-accent-blue" />
              <h3 className="font-heading text-base font-semibold text-white">Endre passord</h3>
            </div>
            <p className="text-sm text-slate-400 mb-5">Oppdater passordet ditt. Du blir logget ut etter endring.</p>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Nåværende passord</label>
                <input
                  type="password"
                  value={pwForm.current_password}
                  onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                  data-testid="current-password"
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Nytt passord</label>
                <input
                  type="password"
                  value={pwForm.new_password}
                  onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                  data-testid="new-password"
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
                <p className="text-xs text-slate-500 mt-1">Minimum 8 tegn.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bekreft nytt passord</label>
                <input
                  type="password"
                  value={pwForm.confirm_password}
                  onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                  data-testid="confirm-password"
                  className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
              </div>
              {pwError && (
                <div className="p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg text-sm text-accent-red">
                  {pwError}
                </div>
              )}
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={pwSaving || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password}
                data-testid="change-password-submit"
                className="flex items-center gap-2 px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {pwSaving ? <Loader size={14} className="animate-spin" /> : <KeyRound size={14} />}
                {pwSaving ? 'Oppdaterer...' : 'Endre passord'}
              </button>
              <p className="text-xs text-slate-500 pt-2 border-t border-navy-700">
                Passordet ditt oppdateres umiddelbart etter lagring.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'team' && (
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-base font-semibold text-white mb-2">Teammedlemmer</h3>
                <p className="text-sm text-slate-400">Brukere som har tilgang til {company?.name || 'denne bedriftens'} data.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowInviteModal(true)}
                data-testid="invite-team-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                <UserPlus size={15} /> Inviter kollega
              </button>
            </div>
            {companyUsers.length > 0 ? (
              <div className="space-y-3">
                {companyUsers.map(u => (
                  <div key={u._id} className="flex items-center justify-between p-4 bg-navy-900 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-accent-blue/20 flex items-center justify-center text-accent-blue text-sm font-bold">
                        {(u.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs px-2 py-1 bg-navy-700 rounded text-slate-300">{u.role === 'admin' ? 'Admin' : 'Bruker'}</span>
                      {u.last_login && <p className="text-[10px] text-slate-600 mt-1">Sist innlogget: {new Date(u.last_login).toLocaleDateString('nb-NO')}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users size={24} className="mx-auto text-slate-600 mb-2" />
                <p className="text-sm text-slate-500">Ingen teammedlemmer funnet.</p>
                <p className="text-xs text-slate-600 mt-1">Kontakt administrator for å legge til brukere.</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button data-testid="settings-save-button" type="submit" disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50">
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>
          {company?.created_at && (
            <span className="text-xs text-slate-600">Sist oppdatert: {new Date(company.created_at).toLocaleDateString('nb-NO')}</span>
          )}
        </div>
      </form>

      {/* Invite team member modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !inviting && setShowInviteModal(false)}>
          <div className="bg-navy-800 border border-navy-600 rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy-600">
              <h3 className="font-heading text-lg font-semibold text-white">Inviter kollega</h3>
              <button type="button" onClick={() => !inviting && setShowInviteModal(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleInvite} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Navn</label>
                <input
                  type="text"
                  required
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="Kari Nordmann"
                  className="w-full px-4 py-2.5 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">E-post</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="kari@bedrift.no"
                  className="w-full px-4 py-2.5 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Rolle</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full px-4 py-2.5 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
                >
                  <option value="user">Bruker</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
              {inviteError && (
                <div className="p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg text-sm text-accent-red">
                  {inviteError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                  className="px-4 py-2.5 text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  data-testid="invite-submit"
                  className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {inviting ? <Loader size={14} className="animate-spin" /> : <UserPlus size={14} />}
                  Send invitasjon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
